<script setup>
import { ref, onMounted } from 'vue'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import ExamBoardsSelection from '@/Components/Forms/ExamBoardsSelection.vue'

const props = defineProps({
  examboards: Array,
  userExamboardIds: Array,
})

const selectedExamboards = ref([])
const loading = ref(false)
const errors = ref({})
const successMessage = ref('')

onMounted(() => {
  selectedExamboards.value = props.userExamboardIds || []
})

const updateSelectedExamboards = (newExamboards) => {
  selectedExamboards.value = newExamboards
}

const saveExamboards = async () => {
  if (selectedExamboards.value.length === 0) {
    errors.value = { selected_examboards: 'Please select at least one exam board' }
    return
  }

  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const response = await fetch('/teacher/settings/examboards', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({ examboard_ids: selectedExamboards.value })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Map backend error keys to component error keys
        errors.value = {
          ...data.errors,
          selected_examboards: data.errors.examboard_ids ? data.errors.examboard_ids[0] : null
        }
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false
      return
    }

    successMessage.value = 'Exam boards updated successfully!'
    loading.value = false

    setTimeout(() => {
      successMessage.value = ''
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">check_circle</span>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">error</span>
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errors.general }}</p>
        </div>
      </div>

      <!-- Examboards Selection -->
      <ExamBoardsSelection
        :model-value="selectedExamboards"
        @update:model-value="updateSelectedExamboards"
        :examboards="examboards"
        :errors="errors"
        title="Exam Boards"
        description="Select the exam boards you're familiar with"
        :show-shadow="false"
        view-mode="simple"
      />

      <!-- Save Button -->
      <div class="flex justify-end pt-6 border-t border-gray-200">
        <button
          @click="saveExamboards"
          :disabled="loading"
          class="px-8 py-3.5 bg-gradient-to-r from-teal-500 to-teal-800 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-800 transition-all shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-600/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-base"
        >
          <span v-if="loading" class="material-symbols-outlined animate-spin mr-2 text-xl">progress_activity</span>
          {{ loading ? 'Saving...' : 'Save Changes' }}
          <span v-if="!loading" class="material-symbols-outlined ml-2 text-xl">check</span>
        </button>
      </div>
    </div>
  </SettingsLayout>
</template>
